import { Box, Card, CardActionArea, CardContent, CardHeader, Chip, Stack, Typography } from '@mui/material';
import { BenefitActivationDTO, BenefitDTOBenefitEnum, BenefitTemplateItemDTO } from 'probonio-shared-ui/api';
import { CUSTOM_BENEFIT_ICONS } from 'probonio-shared-ui/component/icon';
import { apis, useTenantQuery } from 'probonio-shared-ui/module/api';
import { useTenant } from 'probonio-shared-ui/module/me';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useBenefitAttributes } from '../../../extension/interface/benefit-columns.extension.interface';
import { getBenefitIcon } from '../../benefits/BenefitIcon';
import { NON_CASH_BENEFITS, NonCashBenefit } from '../../benefits/nonCashBenefit/const';

interface Props {
  cardItem: BenefitTemplateItemDTO | BenefitActivationDTO;
  actions?: React.ReactNode;
  onClick?: (item: BenefitTemplateItemDTO | BenefitActivationDTO) => void;
  actionChips?: React.ReactNode;
  titleChip?: React.ReactNode;
}

function useBenefitOptionLabels(
  options: BenefitTemplateItemDTO | BenefitActivationDTO,
): { id: string; label: string; value: React.ReactNode }[] | undefined {
  const { t } = useTranslation('benefitsModule');
  const attributes = useBenefitAttributes(options.benefit);

  return attributes?.map(attribute => {
    // Cast to BenefitActivationDTO because BenefitTemplateItemDTO only misses id from BenefitActivationDTO
    const optionValue = attribute.accessorFn!(options as BenefitActivationDTO);
    const renderedValue = attribute.renderer ? attribute.renderer(optionValue) : `${optionValue}`;
    return { id: attribute.id, label: t(attribute.header), value: renderedValue };
  });
}

export const BenefitOptionChips: React.FC<{ benefitOptions: BenefitTemplateItemDTO | BenefitActivationDTO }> = ({ benefitOptions }) => {
  const optionLabels = useBenefitOptionLabels(benefitOptions);

  return (
    <Stack direction="row" spacing={1}>
      {optionLabels?.map(({ id, label, value }) =>
        value || value === 0 ? (
          <Chip
            key={id}
            data-test-id={`attribute-chip-${id}`}
            size="small"
            label={
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.33em',
                }}
              >
                <div>{label}:</div>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                >
                  {value}
                </Box>
              </Box>
            }
          />
        ) : null,
      )}
    </Stack>
  );
};

export const BenefitCard: React.FC<Props> = ({ cardItem, actions, onClick, actionChips, titleChip }) => {
  const { t } = useTranslation('tenantModule');
  const { tenant } = useTenant();

  const { data: customBenefit } = useTenantQuery(
    ['benefits', 'custom', cardItem.customBenefitOptions?.customBenefitId, apis.customBenefits.getCustomBenefit],
    tenantId =>
      apis.customBenefits
        .getCustomBenefit({
          tenantId,
          customBenefitId: cardItem.customBenefitOptions?.customBenefitId || '',
        })
        .then(res => res.data),
    {
      enabled: !!cardItem.customBenefitOptions?.customBenefitId,
      gcTime: 60000,
      staleTime: 60000,
    },
  );

  const assignedNonCash = new Set(cardItem.flexOptions?.nonCashBenefits ?? []);
  const displayedFlexBenefits =
    tenant?.flexBenefits?.filter(
      benefit => !NON_CASH_BENEFITS.includes(benefit as NonCashBenefit) || assignedNonCash.has(benefit as NonCashBenefit),
    ) ?? [];

  const handleClick = useCallback(() => {
    onClick?.(cardItem);
  }, [onClick, cardItem]);

  const Icon = customBenefit ? CUSTOM_BENEFIT_ICONS[customBenefit.iconName] : getBenefitIcon(cardItem.benefit);

  return (
    <Card key={cardItem.benefit} variant="outlined" data-test-id={`benefit-card-${cardItem.benefit}`}>
      <CardActionArea component="div" onClick={handleClick}>
        <CardHeader
          sx={{ py: 1, '& .MuiCardHeader-action': { alignSelf: 'center', mt: 0 } }}
          avatar={<Icon color="primary" fontSize="large" />}
          title={
            <Stack
              direction="row"
              spacing={0.75}
              sx={{
                alignItems: 'center',
                mb: 0.5,
              }}
            >
              <Typography
                sx={{
                  color: 'primary.main',
                }}
              >
                {customBenefit ? customBenefit.title : t(`common:benefit.${cardItem.benefit}`)}
              </Typography>
              {titleChip}
            </Stack>
          }
          subheader={<BenefitOptionChips benefitOptions={cardItem} />}
          action={
            <Stack
              direction="column"
              spacing={0.5}
              sx={{
                alignItems: 'flex-end',
              }}
            >
              {actions}
              {actionChips}
            </Stack>
          }
        />
        {cardItem.benefit === BenefitDTOBenefitEnum.Flex && (
          <CardContent sx={{ paddingLeft: 7.2, paddingTop: 0, paddingBottom: 1 }}>
            <Typography
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('benefitsModule:FLEX.cardSubtitle')}
            </Typography>
            {displayedFlexBenefits.map(benefit => {
              const FlexIcon = getBenefitIcon(benefit);

              return (
                <Stack key={benefit} direction="row" spacing={1}>
                  <FlexIcon color="primary" fontSize="small" />
                  <Typography
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    {t(`common:benefit.${benefit}`)}
                  </Typography>
                </Stack>
              );
            })}
          </CardContent>
        )}
      </CardActionArea>
    </Card>
  );
};
